import { MessagePathPartType } from "@/shared/domain/topics";
import type { TopicData } from "@/shared/state/visualization";
import type * as threeDSchema from "@/shared/state/visualization/schema/v1/three-d";

/**
 * Swaps a binding a layout stored for the topic its rule matched in the
 * workspace the layout is opened on.
 *
 * A stored binding carries the topic, message path and representation ids of
 * the recording the layout was saved on. A binding that kept them would go on
 * drawing that recording wherever the layout is opened, workspace or not.
 */
export type Rebind = (stored: TopicData) => TopicData;

/**
 * A {@link Rebind} over one match of a rule: one topic per requirement, as
 * `LayoutRuleMatcher` hands them back.
 *
 * A stored binding is found among them by topic name and message path — the
 * attribute names and the slice bounds — which is what a requirement is
 * written from (`LayoutDefinitionBuilder.createRequirementFromTopicData`) and
 * what the matcher compares. Ids are left out because they are exactly what
 * differs between recordings, and data types because they decide whether a
 * topic matches at all, not which of the matched ones a binding is. An
 * open-ended slice end is Infinity in memory and null once stored, and
 * `JSON.stringify` writes both as null, so the two spellings find each other.
 *
 * A binding the match holds nothing for comes back as it was stored: one its
 * rule never required, as a rotation's and a vector field's were not before
 * their topics were harvested into the rule's requirements.
 */
export function rebinderFor(matched: readonly TopicData[]): Rebind {
  const byPath = new Map<string, TopicData>();
  for (const data of matched) {
    const key = pathKey(data);
    if (!byPath.has(key)) {
      byPath.set(key, data);
    }
  }
  return (stored) => byPath.get(pathKey(stored)) ?? stored;
}

/** A model's joint values, each bound joint rebound; any other mesh as is. */
export function rebindMesh(
  mesh: threeDSchema.Mesh,
  rebind: Rebind,
): threeDSchema.Mesh {
  // No `jointValues: undefined` for a model without any: `diffVizState`
  // compares with `lodash.isEqual`, which tells an absent key from an
  // `undefined` one.
  if (mesh.type !== "model" || mesh.jointValues === undefined) {
    return mesh;
  }
  return {
    ...mesh,
    jointValues: Object.fromEntries(
      Object.entries(mesh.jointValues).map(([name, jointValue]) => [
        name,
        { ...jointValue, value: rebindChannel(jointValue.value, rebind) },
      ]),
    ),
  };
}

/** A rotation with each of its bound channels rebound. */
export function rebindRotation(
  rotation: threeDSchema.Rotation,
  rebind: Rebind,
): threeDSchema.Rotation {
  switch (rotation.type) {
    case "euler":
      return {
        ...rotation,
        roll: rebindChannel(rotation.roll, rebind),
        pitch: rebindChannel(rotation.pitch, rebind),
        yaw: rebindChannel(rotation.yaw, rebind),
      };
    case "axis_angle":
      return { ...rotation, angle: rebindChannel(rotation.angle, rebind) };
    case "quaternion":
      return {
        ...rotation,
        x: rebindChannel(rotation.x, rebind),
        y: rebindChannel(rotation.y, rebind),
        z: rebindChannel(rotation.z, rebind),
        w: rebindChannel(rotation.w, rebind),
      };
  }
}

/** A vector field with each of its bound axes rebound. */
export function rebindVectorField(
  vectorField: threeDSchema.VectorField,
  rebind: Rebind,
): threeDSchema.VectorField {
  return {
    ...vectorField,
    forward: rebindChannel(vectorField.forward, rebind),
    right: rebindChannel(vectorField.right, rebind),
    up: rebindChannel(vectorField.up, rebind),
  };
}

/** A channel held at a constant has nothing to rebind. */
function rebindChannel(
  value: TopicData | number,
  rebind: Rebind,
): TopicData | number {
  return typeof value === "number" ? value : rebind(value);
}

function pathKey({ topic, messagePath }: TopicData): string {
  return JSON.stringify([
    topic.name,
    messagePath.parts.map((part) =>
      part.type === MessagePathPartType.Attr
        ? [part.type, part.attribute]
        : [part.type, part.start, part.end ?? null],
    ),
  ]);
}
